import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { Button, TailwindDialog, DialogContent } from '@/components/ui';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';

// Clé Stripe CashLive (env) : l'achat de crédits est toujours encaissé sur notre compte Stripe
const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;

// TVA 20% pour l'achat de crédits
const CREDITS_PURCHASE_VAT_RATE = 0.2;

// Helpers
const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
};

const amountTtcCents = (amountCentsHT: number) =>
  Math.round(amountCentsHT * (1 + CREDITS_PURCHASE_VAT_RATE));

// Purchase Form Component (inside Stripe Elements)
const PurchaseForm: React.FC<{
  amountCentsHT: number;
  amountCentsTTC: number;
  onSuccess: () => void;
  onCancel: () => void;
  organizationId: string;
  paymentIntentId: string;
}> = ({ amountCentsHT, amountCentsTTC, onSuccess, onCancel, organizationId, paymentIntentId }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message || t('admin.credits.paymentFailed', 'Échec du paiement'));
        setIsProcessing(false);
        return;
      }

      // Confirm purchase on backend using unified endpoint
      const response = await fetch(`${FUNCTIONS_BASE_URL}/appCreditsConfirmPurchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          paymentIntentId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success(t('admin.credits.purchaseSuccess', '{{amount}} crédits ajoutés avec succès!', {
          amount: formatCurrency(amountCentsHT),
        }));
        onSuccess();
      } else {
        throw new Error(result.error || t('admin.credits.purchaseFailed', 'Échec de l\'achat'));
      }
    } catch (err: any) {
      setError(err.message);
      toast.error(t('admin.credits.paymentError', 'Erreur lors du paiement'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        onReady={() => setPaymentElementReady(true)}
        onLoadError={() => setPaymentElementReady(false)}
      />
      
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1"
        >
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !elements || !paymentElementReady || isProcessing}
          className="flex-1"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('admin.credits.processing', 'Traitement...')}
            </>
          ) : (
            t('admin.credits.payAmount', 'Payer {{amount}}', { amount: formatCurrency(amountCentsTTC) })
          )}
        </Button>
      </div>
    </form>
  );
};

// Main Modal Component
interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  currentBalance?: number; // in cents
  onSuccess?: () => void; // Callback after successful purchase
  initialAmount?: number; // Optional initial amount in cents for quick purchase
}

export const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  currentBalance = 0,
  onSuccess,
  initialAmount,
}) => {
  const { t } = useTranslation();
  const [purchaseAmount, setPurchaseAmount] = useState(initialAmount || 2500); // 25€ in cents
  
  // Stripe state
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);

  // Predefined amounts
  const predefinedAmounts = [
    { cents: 1000, label: '10€' },
    { cents: 2500, label: '25€' },
    { cents: 5000, label: '50€' },
    { cents: 10000, label: '100€' },
  ];

  // Initialize Stripe when opening purchase modal
  const handleOpenPurchaseModal = async () => {
    if (!organizationId) return;

    try {
      setIsCreatingPayment(true);

      // Create payment intent using unified endpoint
      const response = await fetch(`${FUNCTIONS_BASE_URL}/appCreditsPurchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          amountCents: purchaseAmount,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || t('admin.credits.createPaymentError', 'Échec de la création du paiement'));
      }

      if (!STRIPE_PUBLIC_KEY) {
        throw new Error(
          t('admin.credits.stripeNotConfigured', 'Stripe non configuré : VITE_STRIPE_PUBLIC_KEY manquant dans .env')
        );
      }

      // Toujours utiliser notre clé Stripe (CashLive) pour l'achat de crédits – le PaymentIntent est créé côté backend avec le même compte
      setStripePromise(loadStripe(STRIPE_PUBLIC_KEY));
      setClientSecret(result.clientSecret);
      setPaymentIntentId(result.paymentIntentId);
    } catch (error: any) {
      console.error('Error creating payment:', error);
      toast.error(error.message || t('admin.credits.createPaymentError', 'Erreur lors de la création du paiement'));
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handlePurchaseSuccess = () => {
    setClientSecret(null);
    setPaymentIntentId(null);
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleClosePurchaseModal = () => {
    onClose();
    setClientSecret(null);
    setPaymentIntentId(null);
  };

  // Auto-init payment when initialAmount is provided
  React.useEffect(() => {
    if (isOpen && initialAmount) {
      setPurchaseAmount(initialAmount);
      // Auto-trigger payment initialization for quick purchase
      const timer = setTimeout(() => {
        handleOpenPurchaseModal();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initialAmount]);

  // Reset when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setPaymentIntentId(null);
      setPurchaseAmount(initialAmount || 2500);
    }
  }, [isOpen, initialAmount]);

  return (
    <TailwindDialog
      isOpen={isOpen}
      onClose={handleClosePurchaseModal}
      title={t('admin.credits.buyCredits', 'Acheter des crédits')}
      maxWidth="sm"
      variant="themed"
    >
      <DialogContent className="p-5 flex-1 min-h-0 overflow-y-auto">
        <div className="mb-5 p-3 bg-background-secondary rounded-lg border border-border">
          <p className="text-foreground-secondary text-xs mb-1">
            {t('admin.credits.currentBalance', 'Solde actuel')}
          </p>
          <p className="text-foreground text-lg font-bold">{formatCurrency(currentBalance)}</p>
          <p className="text-foreground-secondary text-xs mt-2">
            {t('admin.credits.balanceDescription', 'Ces crédits peuvent être utilisés pour l\'expédition et l\'IA.')}
          </p>
        </div>

        {/* Amount Selection */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-foreground">
              {t('admin.credits.amountToCredit', 'Montant à créditer')}
            </label>
            <span className="text-lg font-bold text-primary">
              {formatCurrency(purchaseAmount)} HT
            </span>
          </div>
          <p className="text-foreground-secondary text-xs mb-3">
            {t('admin.credits.vatIncluded', 'TVA 20% incluse')} :{' '}
            <span className="font-semibold text-foreground">
              {formatCurrency(amountTtcCents(purchaseAmount))} TTC
            </span>
          </p>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {predefinedAmounts.map((amount) => (
              <button
                key={amount.cents}
                type="button"
                onClick={() => {
                  setPurchaseAmount(amount.cents);
                  // Reset payment intent when amount changes
                  setClientSecret(null);
                  setPaymentIntentId(null);
                }}
                className={`px-3 py-2 rounded-lg border-2 transition-all text-sm font-semibold ${
                  purchaseAmount === amount.cents
                    ? 'border-primary bg-primary text-white shadow-md scale-105'
                    : 'border-border hover:border-primary hover:bg-primary-10 text-foreground'
                }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <label className="text-xs text-foreground-secondary block mb-1">
              {t('admin.credits.customAmount', 'Ou montant personnalisé')}
            </label>
            <input
              type="number"
              min={10}
              step={5}
              value={purchaseAmount / 100}
              onChange={(e) => {
                const value = Math.max(10, Number(e.target.value)) * 100;
                setPurchaseAmount(value);
                setClientSecret(null);
                setPaymentIntentId(null);
              }}
              placeholder="Montant personnalisé"
              className="w-full px-3 py-2 border border-border rounded-lg text-right pr-8 text-sm bg-surface text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-20"
            />
            <span className="absolute right-3 bottom-2 text-foreground-secondary text-sm font-medium">
              €
            </span>
          </div>
        </div>

        {/* Stripe Payment Form */}
        {stripePromise && clientSecret && paymentIntentId && organizationId ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PurchaseForm
              amountCentsHT={purchaseAmount}
              amountCentsTTC={amountTtcCents(purchaseAmount)}
              onSuccess={handlePurchaseSuccess}
              onCancel={handleClosePurchaseModal}
              organizationId={organizationId}
              paymentIntentId={paymentIntentId}
            />
          </Elements>
        ) : (
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleClosePurchaseModal} className="flex-1">
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button 
              onClick={handleOpenPurchaseModal} 
              disabled={isCreatingPayment}
              isLoading={isCreatingPayment}
              className="flex-1"
            >
              {!isCreatingPayment && t('admin.credits.continueToPayment', 'Continuer vers le paiement')}
            </Button>
          </div>
        )}
      </DialogContent>
    </TailwindDialog>
  );
};

export default BuyCreditsModal;

